import { useEffect, useMemo, useState } from 'react';

import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { Tooltip } from '../../../../components/Tooltip';
import { todayCalendarDate } from '../../../../components/calendar/monthGrid';
import { Checkbox } from '../../../../components/inputs/Checkbox';
import { DateField } from '../../../../components/inputs/DateField';
import { FormFieldset } from '../../../../components/inputs/FormFieldset';
import { Textarea } from '../../../../components/inputs/Textarea';
import { useToast } from '../../../../components/toastContext';
import { ApiError } from '../../../../lib/apiError';
import { focusValidationError } from '../../../../lib/validationFocus';
import { usePersonNames, type PersonNames } from '../../personnel/usePersonNames';
import { nomineeName } from '../nomineeName';
import { evaluationsApi } from '../praiseApi';
import type { AwardDetail, AwardNomineeListItem, AwardStageGate } from '../praiseTypes';

/** The candidates a stage decides among — the qualified for a deliberation, the winners for the awarding. One award's are a bounded set. */
function useCandidates(award: AwardDetail, stage: AwardStageGate) {
  const [rows, setRows] = useState<AwardNomineeListItem[] | null>(null);
  const [failed, setFailed] = useState<ApiError | null>(null);

  useEffect(() => {
    const controller = new AbortController();

    evaluationsApi
      .listNominees(award.id, { stage, sort: 'score:desc', pageSize: 100 }, controller.signal)
      .then((page) => {
        if (!controller.signal.aborted) setRows(page.items);
      })
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;
        setFailed(caught instanceof ApiError ? caught : new ApiError('Could not load the nominees.', 0));
      });

    return () => controller.abort();
  }, [award.id, stage]);

  const personIds = useMemo(() => (rows ?? []).map((n) => n.personId).filter((id): id is string => id != null), [rows]);
  const names = usePersonNames(personIds, evaluationsApi.names);

  return { rows, failed, names };
}

/**
 * The candidates as a ticked list — rank, name, average — ordered by the server's ranking. A row the
 * reader cannot tick says why beside it rather than vanishing.
 */
function CandidateList({
  id,
  rows,
  names,
  maxTotal,
  chosen,
  onToggle,
  disabledReason,
}: {
  id: string;
  rows: AwardNomineeListItem[];
  names: PersonNames;
  maxTotal: number;
  chosen: string[];
  onToggle: (nomineeId: string, on: boolean) => void;
  disabledReason: (row: AwardNomineeListItem) => string | null;
}) {
  return (
    <ul id={id} className="divide-y divide-slate-200 rounded-lg border border-slate-200">
      {rows.map((row) => {
        const reason = disabledReason(row);
        const ticked = chosen.includes(row.id);

        return (
          <li key={row.id}>
            <label htmlFor={`${id}-${row.id}`} className="flex items-center gap-3 px-3 py-2.5 text-sm text-slate-600">
              <Checkbox
                id={`${id}-${row.id}`}
                checked={ticked}
                onChange={(e) => onToggle(row.id, e.target.checked)}
                disabled={!ticked && reason != null}
              />
              <span className="w-10 shrink-0 font-mono text-xs text-slate-500">{row.rank != null ? `#${row.rank}` : '—'}</span>
              <span className="min-w-0 flex-1 truncate font-medium text-slate-800">{nomineeName(row.personId, names) ?? '—'}</span>
              <span className="shrink-0 font-mono text-xs text-slate-500">
                {row.averageScore != null ? `${row.averageScore.toFixed(2)} / ${maxTotal}` : '—'}
              </span>
            </label>
          </li>
        );
      })}
    </ul>
  );
}

/**
 * DELIBERATION — names the winners from the QUALIFIED, scored nominees, at most the award's number of
 * winners, with remarks. Re-savable until the final awarding, and saving FIXES qualification, scores and
 * the nominee list. Its documents are attached on the stage's card, not here — they outlive one save.
 */
export function DeliberationModal({
  award,
  onClose,
  onSaved,
}: {
  award: AwardDetail;
  onClose: () => void;
  onSaved: (next: AwardDetail) => void;
}) {
  const toast = useToast();
  const { rows, failed, names } = useCandidates(award, 'deliberation');

  const [chosen, setChosen] = useState<string[] | null>(null);
  const [remarks, setRemarks] = useState(award.deliberationRemarks ?? '');
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  // SEEDED from the record once the candidates arrive — a re-save starts from what was decided.
  const winners = chosen ?? (rows ?? []).filter((n) => n.isWinner).map((n) => n.id);
  const full = winners.length >= award.winnerCount;

  const canSave = winners.length > 0 && confirmed && !saving;
  const saveDisabledReason = saving
    ? null
    : winners.length === 0
      ? 'Name at least one winner.'
      : !confirmed
        ? 'Tick the confirm box to submit.'
        : null;

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    try {
      const next = await evaluationsApi.deliberate(award.id, { winnerNomineeIds: winners, remarks: remarks.trim() || null });
      toast.success(`The deliberation was recorded — ${winners.length} ${winners.length === 1 ? 'winner' : 'winners'}.`);
      onSaved(next);
    } catch (caught) {
      const failure = caught instanceof ApiError ? caught : new ApiError('Could not record the deliberation.', 0);

      setError(failure);
      focusValidationError(failure, { idPrefix: 'award-deliberation', fieldIds: { winnerNomineeIds: 'award-deliberation-winners' } });
    } finally {
      setSaving(false);
    }
  }

  // The TOP N by rank — the deliberation's usual answer, and the cap exercised.
  function generate() {
    setChosen((rows ?? []).filter((n) => n.personId != null).slice(0, award.winnerCount).map((n) => n.id));
    setRemarks('The panel deliberated on the ranked results and named the winners by majority.');
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={`Deliberation — ${award.categoryName}`}
      size="xl"
      id="award-deliberation-modal"
      loading={rows === null && failed === null}
      autogenerate={{ onGenerate: generate, disabled: saving || rows === null }}
      confirm={{ id: 'award-deliberation-confirm', checked: confirmed, onChange: setConfirmed, disabled: saving }}
      footer={
        <>
          <Button id="award-deliberation-cancel" variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Tooltip disabledReason={saveDisabledReason}>
            <Button id="award-deliberation-submit" variant="primary" onClick={() => void save()} disabled={!canSave} loading={saving} loadingLabel="Submitting">
              Submit
            </Button>
          </Tooltip>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error || failed ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{(error ?? failed)!.message}</p>
          </div>
        ) : null}

        <FormFieldset legend={`Winners — ${winners.length} of ${award.winnerCount}`}>
          <CandidateList
            id="award-deliberation-winners"
            rows={rows ?? []}
            names={names}
            maxTotal={award.maxTotal}
            chosen={winners}
            onToggle={(id, on) => setChosen(on ? [...winners, id] : winners.filter((w) => w !== id))}
            disabledReason={(row) =>
              row.personId == null
                ? 'This nominee is above your clearance.'
                : full
                  ? award.winnerCount === 1
                    ? 'This award names a single winner.'
                    : `This award names at most ${award.winnerCount} winners.`
                  : null
            }
          />
        </FormFieldset>

        <Textarea
          id="award-deliberation-remarks"
          label="Remarks"
          description="What the panel decided and why — the deliberation's record."
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
          error={error?.fieldError('remarks')}
          disabled={saving}
        />

        {/* The CONSEQUENCE, read before the tick. */}
        <p className="text-xs text-slate-500">
          Recording the deliberation fixes every nominee&apos;s qualification and scores, and no one more can be nominated. It can
          be saved again to change the winners until the final awarding.
        </p>
      </div>
    </Modal>
  );
}

/**
 * FINAL AWARDING — approves winners from the deliberation's, the date the award was conferred, remarks.
 * **ONE-WAY**: it writes a Recognition on each approved winner's 201 file and closes every stage.
 */
export function FinalAwardingModal({
  award,
  onClose,
  onSaved,
}: {
  award: AwardDetail;
  onClose: () => void;
  onSaved: (next: AwardDetail) => void;
}) {
  const toast = useToast();
  const { rows, failed, names } = useCandidates(award, 'awarding');

  const [approved, setApproved] = useState<string[]>([]);
  const [awardedOn, setAwardedOn] = useState('');
  const [remarks, setRemarks] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const canSave = approved.length > 0 && awardedOn !== '' && confirmed && !saving;
  const saveDisabledReason = saving
    ? null
    : approved.length === 0
      ? 'Approve at least one winner.'
      : awardedOn === ''
        ? 'Give the date the award was conferred.'
        : !confirmed
          ? 'Tick the confirm box to submit.'
          : null;

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    try {
      const next = await evaluationsApi.finalize(award.id, { awardedNomineeIds: approved, awardedOn, remarks: remarks.trim() || null });
      toast.success(`${award.categoryName} was awarded to ${approved.length} ${approved.length === 1 ? 'winner' : 'winners'}.`);
      onSaved(next);
    } catch (caught) {
      const failure = caught instanceof ApiError ? caught : new ApiError('Could not record the final awarding.', 0);

      setError(failure);
      focusValidationError(failure, { idPrefix: 'award-final', fieldIds: { awardedNomineeIds: 'award-final-approved' } });
    } finally {
      setSaving(false);
    }
  }

  function generate() {
    setApproved((rows ?? []).filter((n) => n.personId != null).map((n) => n.id));
    setAwardedOn(todayCalendarDate());
    setRemarks('Approved by the head of agency and conferred at the recognition rites.');
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={`Final awarding — ${award.categoryName}`}
      size="xl"
      id="award-final-modal"
      loading={rows === null && failed === null}
      autogenerate={{ onGenerate: generate, disabled: saving || rows === null }}
      confirm={{ id: 'award-final-confirm', checked: confirmed, onChange: setConfirmed, disabled: saving }}
      footer={
        <>
          <Button id="award-final-cancel" variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Tooltip disabledReason={saveDisabledReason}>
            <Button id="award-final-submit" variant="primary" onClick={() => void save()} disabled={!canSave} loading={saving} loadingLabel="Submitting">
              Submit
            </Button>
          </Tooltip>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error || failed ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{(error ?? failed)!.message}</p>
          </div>
        ) : null}

        <FormFieldset legend={`Approved winners — ${approved.length} of ${(rows ?? []).length}`}>
          <CandidateList
            id="award-final-approved"
            rows={rows ?? []}
            names={names}
            maxTotal={award.maxTotal}
            chosen={approved}
            onToggle={(id, on) => setApproved((current) => (on ? [...current, id] : current.filter((a) => a !== id)))}
            disabledReason={(row) => (row.personId == null ? 'This nominee is above your clearance.' : null)}
          />
        </FormFieldset>

        <div className="grid gap-4 sm:grid-cols-2">
          <DateField
            id="award-final-awarded-on"
            label="Awarded on"
            required
            description="The date the award was conferred — what each winner's 201 file records."
            value={awardedOn}
            onChange={setAwardedOn}
            max={todayCalendarDate()}
            error={error?.fieldError('awardedOn')}
            disabled={saving}
          />
        </div>

        <Textarea
          id="award-final-remarks"
          label="Remarks"
          description="Who approved it and how it was conferred — the awarding's record."
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
          error={error?.fieldError('remarks')}
          disabled={saving}
        />

        {/* The CONSEQUENCE, read before the tick — this one cannot be undone. */}
        <p className="text-xs text-slate-500">
          This is final. Each approved winner gets a Recognition entry on their 201 file — {award.categoryName}, awarded by PRAISE, on
          the date above — and every stage of this award is closed.
        </p>
      </div>
    </Modal>
  );
}
